import { buildAlgoliaSearchAttribution } from '../algolia/attribution.js';
import { getHitSwatches } from '../algolia/swatch-source.js';

function formatMoney(value) {
  if (typeof window !== 'undefined' && typeof window.formattedPrice === 'function') {
    return window.formattedPrice(value);
  }

  return '';
}

function getCustomerDiscountValue(tags) {
  if (typeof window !== 'undefined' && typeof window.customerDiscount === 'function') {
    return Number(window.customerDiscount(tags)) || 0;
  }

  return 0;
}

function getDiscountedPrice(priceValue, compareAtPriceValue, customerDiscountValue) {
  const price = Number(priceValue);
  const compareAtPrice = Number(compareAtPriceValue);

  if (!Number.isFinite(price)) {
    return 0;
  }

  if (Number.isFinite(compareAtPrice) && compareAtPrice > 0) {
    return Math.min(compareAtPrice * (1 - customerDiscountValue), price);
  }

  return price * (1 - customerDiscountValue);
}

function getDisplayComparePrice(priceValue, compareAtPriceValue) {
  const price = Number(priceValue);
  const compareAtPrice = Number(compareAtPriceValue);

  if (Number.isFinite(compareAtPrice) && compareAtPrice > 0) {
    return compareAtPrice;
  }

  return Number.isFinite(price) ? price : 0;
}

function escapeHtml(value) {
  if (value == null) {
    return '';
  }

  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function parseMetaobjectIds(value) {
  if (!value) {
    return [];
  }

  let parsed = value;

  if (typeof value === 'string') {
    try {
      parsed = JSON.parse(value);
    } catch (error) {
      parsed = [value];
    }
  }

  const list = Array.isArray(parsed) ? parsed : [parsed];

  // Values arrive as "gid://shopify/Metaobject/<id>"; the lookup is keyed by the numeric id.
  return list.map((entry) => String(entry).split('/').pop()).filter(Boolean);
}

// `hit.meta.custom.product_badge` is the natively synced metafield holding metaobject ids. They are
// mapped through the badge lookup that `main-search.liquid` emits from `shop.metaobjects.badges`.
function getProductBadges(hit, config) {
  const badgeMap = (config && config.productBadgeMap) || {};
  const ids = parseMetaobjectIds(getValueByPath(hit, 'meta.custom.product_badge'));

  return ids.map((id) => badgeMap[id]).filter((badge) => badge && badge.text);
}

function renderProductBadges(hit, config) {
  const badges = getProductBadges(hit, config);

  if (!badges.length) {
    return '';
  }

  const items = badges
    .map((badge) => {
      const style = [
        badge.background_color ? `background-color: ${escapeHtml(badge.background_color)}` : '',
        badge.text_color ? `color: ${escapeHtml(badge.text_color)}` : '',
      ]
        .filter(Boolean)
        .join('; ');

      return `<li class="pc__feature-badge"${style ? ` style="${style}"` : ''}>${escapeHtml(badge.text)}</li>`;
    })
    .join('');

  return `<ul class="pc__feature-badges search-hit-card__feature-badges">${items}</ul>`;
}

function getValueByPath(source, path) {
  if (!source || !path) {
    return undefined;
  }

  return path.split('.').reduce((value, key) => {
    if (value == null) {
      return undefined;
    }

    return value[key];
  }, source);
}

function getProductUrl(hit) {
  if (hit.url) {
    return hit.url;
  }

  if (hit.handle) {
    return `/products/${hit.handle}`;
  }

  return '#';
}

function buildAttributionClickHandler(hit, indexName) {
  const attribution = buildAlgoliaSearchAttribution({
    objectID: hit.objectID || null,
    productID: hit.id || null,
    index: hit.__indexName || indexName || null,
    queryID: hit.__queryID || null,
    position: hit.__position || null,
  });

  return `window.setAlgoliaSearchAttribution && window.setAlgoliaSearchAttribution(${escapeHtml(JSON.stringify(attribution))})`;
}

function getProductImage(hit) {
  const defaultSwatch = getDefaultSwatch(hit);
  const featuredImage = hit.featured_image;

  return (
    (defaultSwatch && defaultSwatch.image) ||
    (defaultSwatch && defaultSwatch.hover_image) ||
    hit.image ||
    hit.hover_image ||
    (featuredImage && featuredImage.url) ||
    featuredImage ||
    hit.thumbnail ||
    ''
  );
}

function getProductHoverImage(hit) {
  const defaultSwatch = getDefaultSwatch(hit);

  return (defaultSwatch && defaultSwatch.hover_image) || (defaultSwatch && defaultSwatch.image) || hit.hover_image || '';
}

function getSwatchColorKey(swatch) {
  if (!swatch || typeof swatch.color !== 'string') {
    return '';
  }

  return swatch.color.trim().toLowerCase();
}

function mergeSwatchGroup(primary, candidate) {
  const preferred = primary.available ? primary : candidate.available ? candidate : primary;

  return {
    ...preferred,
    color: preferred.color || primary.color || candidate.color || '',
    available: Boolean(primary.available || candidate.available),
    price: preferred.price || primary.price || candidate.price,
    compare_at_price: preferred.compare_at_price || primary.compare_at_price || candidate.compare_at_price,
    image: preferred.image || primary.image || candidate.image,
    hover_image: preferred.hover_image || primary.hover_image || candidate.hover_image,
    swatch_image: preferred.swatch_image || primary.swatch_image || candidate.swatch_image,
    variant_id: preferred.variant_id || primary.variant_id || candidate.variant_id,
  };
}

function getProductSwatches(hit) {
  return getHitSwatches(hit)
    .filter(Boolean)
    .reduce((mergedSwatches, swatch) => {
      const colorKey = getSwatchColorKey(swatch);

      if (!colorKey) {
        mergedSwatches.push(swatch);
        return mergedSwatches;
      }

      const existingIndex = mergedSwatches.findIndex((entry) => getSwatchColorKey(entry) === colorKey);

      if (existingIndex === -1) {
        mergedSwatches.push(swatch);
        return mergedSwatches;
      }

      mergedSwatches[existingIndex] = mergeSwatchGroup(mergedSwatches[existingIndex], swatch);
      return mergedSwatches;
    }, [])
    .filter((swatch) => swatch.available);
}

function getDefaultSwatch(hit) {
  const swatches = getProductSwatches(hit);

  return swatches[0];
}

function findSwatchByColorGroup(swatches, activeColorGroups) {
  if (!activeColorGroups || !activeColorGroups.length || !swatches.length) {
    return null;
  }

  const groups = new Set(activeColorGroups.map((g) => g.toLowerCase().trim()));

  return swatches.find((swatch) => {
    const group = (swatch.color_group || '').toLowerCase().trim();
    return group && groups.has(group);
  }) || null;
}

function isGiftCard(hit) {
  if (hit.gift_card === true) return true;
  if (typeof hit.product_type === 'string' && hit.product_type.toLowerCase().includes('gift card')) return true;
  if (Array.isArray(hit.tags) && hit.tags.some((t) => typeof t === 'string' && t.toLowerCase().includes('gift card'))) return true;
  return false;
}

function getProductTitle(hit) {
  return hit.title || hit.name || 'Untitled product';
}

function resizeShopifyImage(url, width) {
  if (!url || typeof url !== 'string') return url || '';

  try {
    const u = new URL(url.startsWith('//') ? `https:${url}` : url);

    if (!u.hostname.includes('cdn.shopify.com')) return url;

    u.searchParams.set('width', String(width));
    return u.toString();
  } catch (e) {
    return url;
  }
}

function buildSrcset(url, widths) {
  if (!url) return '';
  return widths.map((w) => `${escapeHtml(resizeShopifyImage(url, w))} ${w}w`).join(', ');
}

const PRODUCT_IMAGE_WIDTHS = [300, 450, 600, 800];
const PRODUCT_IMAGE_SIZES = '(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw';

function getSwatchPrimaryImage(swatch, fallbackImage) {
  return swatch.image || swatch.hover_image || fallbackImage || '';
}

function getSwatchHoverImage(swatch, fallbackHoverImage, fallbackImage) {
  return swatch.hover_image || swatch.image || fallbackHoverImage || fallbackImage || '';
}

export function renderStatsText(data) {
  if (!data.hasManyResults && !data.nbHits) {
    return 'No results';
  }

  if (!data.query) {
    return `${data.nbHits.toLocaleString()} Results`;
  }

  return `${data.nbHits.toLocaleString()} Results for "${data.query}"`;
}

let hitRenderIndex = 0;

export function resetHitRenderIndex() {
  hitRenderIndex = 0;
}

export function renderProductHit(hit, config, activeColorGroups) {
  const currentIndex = hitRenderIndex++;
  const isAboveFold = currentIndex < 4;
  const url = getProductUrl(hit);
  const title = getProductTitle(hit);
  const swatches = getProductSwatches(hit);
  const refinedSwatch = findSwatchByColorGroup(swatches, activeColorGroups);
  const activeSwatch = refinedSwatch || getDefaultSwatch(hit);

  const hitImage = hit.image || hit.hover_image || (hit.featured_image && hit.featured_image.url) || hit.featured_image || hit.thumbnail || '';
  const hitHoverImage = hit.hover_image || '';
  const image = activeSwatch ? getSwatchPrimaryImage(activeSwatch, hitImage) : hitImage;
  const hoverImage = activeSwatch ? getSwatchHoverImage(activeSwatch, hitHoverImage, hitImage) : hitHoverImage;

  const mappedPriceValue = getValueByPath(hit, config.attributes.price);
  const hitPrice = mappedPriceValue == null ? hit.price : mappedPriceValue;
  const rawPriceValue = (activeSwatch && activeSwatch.price) || hitPrice;
  const rawCompareAtPriceValue = (activeSwatch && activeSwatch.compare_at_price) || hit.compare_at_price;
  const customerDiscountValue = getCustomerDiscountValue(hit.tags);
  const displayPriceValue = getDiscountedPrice(rawPriceValue, rawCompareAtPriceValue, customerDiscountValue);
  const displayCompareAtPriceValue = getDisplayComparePrice(rawPriceValue, rawCompareAtPriceValue);
  const price = formatMoney(displayPriceValue);
  const compareAtPrice = formatMoney(displayCompareAtPriceValue);
  const hasComparePrice = Number(displayCompareAtPriceValue) > Number(displayPriceValue);

  const loadingAttr = isAboveFold ? 'eager' : 'lazy';
  const priorityAttr = isAboveFold ? ' fetchpriority="high"' : '';
  const primarySrcset = buildSrcset(image, PRODUCT_IMAGE_WIDTHS);
  const primarySrc = resizeShopifyImage(image, 600);
  const clickHandler = buildAttributionClickHandler(hit, config.indexName);

  return `
    <article class="search-hit-card" data-swatchable-card
      data-insights-object-id="${escapeHtml(hit.objectID || '')}"
      data-insights-position="${hit.__position || ''}"
      data-insights-query-id="${escapeHtml(hit.__queryID || '')}">
      <a class="search-hit-card__media" href="${escapeHtml(url)}" onclick="${clickHandler}">
        ${
  image
    ? `
          <span class="search-hit-card__image-stack">
            <img class="search-hit-card__image search-hit-card__image--primary" src="${escapeHtml(primarySrc)}"${primarySrcset ? ` srcset="${primarySrcset}" sizes="${PRODUCT_IMAGE_SIZES}"` : ''} alt="${escapeHtml(title)}" width="600" height="769" loading="${loadingAttr}"${priorityAttr} decoding="async">
            ${hoverImage && hoverImage !== image
    ? `<img class="search-hit-card__image search-hit-card__image--hover" src="${escapeHtml(resizeShopifyImage(hoverImage, 600))}" alt="" loading="lazy" fetchpriority="low" aria-hidden="true" decoding="async">`
    : '<img class="search-hit-card__image search-hit-card__image--hover" alt="" aria-hidden="true" hidden>'}
          </span>
        `
    : '<div class="search-hit-card__image-placeholder"></div>'
}
      </a>
      <div class="search-hit-card__content">
        ${
  swatches.length && !isGiftCard(hit)
    ? `
          <div class="search-hit-card__swatches" aria-label="Available colors">
            ${swatches.map((swatch) => {
    const isActive = activeSwatch && getSwatchColorKey(activeSwatch) === getSwatchColorKey(swatch);
    const swatchPriceVal = swatch.price || hitPrice;
    const swatchCompareVal = swatch.compare_at_price || hit.compare_at_price;
    const swatchDisplayPriceVal = getDiscountedPrice(swatchPriceVal, swatchCompareVal, customerDiscountValue);
    const swatchDisplayCompareVal = getDisplayComparePrice(swatchPriceVal, swatchCompareVal);
    const swatchHasSale = Number(swatchDisplayCompareVal) > Number(swatchDisplayPriceVal);

    return `
              <button
                type="button"
                class="search-hit-card__swatch${swatch.available ? '' : ' search-hit-card__swatch--unavailable'}${isActive ? ' is-active' : ''}"
                title="${escapeHtml(swatch.color || '')}"
                aria-label="${escapeHtml(`${swatch.color || 'Color'}${swatch.available ? '' : ' unavailable'}`)}"
                aria-pressed="${isActive ? 'true' : 'false'}"
                data-swatch-trigger
                data-color="${escapeHtml(swatch.color || '')}"
                data-image="${escapeHtml(getSwatchPrimaryImage(swatch, hitImage))}"
                data-hover-image="${escapeHtml(getSwatchHoverImage(swatch, hitHoverImage, hitImage))}"
                data-price="${escapeHtml(formatMoney(swatchDisplayPriceVal))}"
                data-compare-at-price="${swatchHasSale ? escapeHtml(formatMoney(swatchDisplayCompareVal)) : ''}"
              >
                ${swatch.swatch_image
    ? `<img class="search-hit-card__swatch-image" src="${escapeHtml(resizeShopifyImage(swatch.swatch_image, 40))}" alt="${escapeHtml(swatch.color || '')}" width="20" height="20" loading="lazy" decoding="async">`
    : '<span class="search-hit-card__swatch-fallback" aria-hidden="true"></span>'}
              </button>
            `;
  }).join('')}
          </div>
        `
    : ''
}
        <h2 class="search-hit-card__title">
          <a href="${escapeHtml(url)}" onclick="${clickHandler}">${escapeHtml(title)}</a>
        </h2>
        ${renderProductBadges(hit, config)}
        <div class="search-hit-card__price">
          <span class="search-hit-card__price-current${hasComparePrice ? ' search-hit-card__price-current--sale' : ''}">${escapeHtml(price)}</span>
          ${
  hasComparePrice
    ? `<span class="search-hit-card__price-compare">${escapeHtml(compareAtPrice)}</span>`
    : ''
}
        </div>
      </div>
    </article>
  `;
}

export function renderEmptyState(results, config) {
  const noResults = (config && config.noResults) || {};
  const heading = noResults.heading || 'No products found';
  const query = results.query ? ` for "${escapeHtml(results.query)}"` : '';
  const subtext = noResults.subtext || '';
  const collections = Array.isArray(noResults.collections) ? noResults.collections : [];

  const collectionsHtml = collections.length
    ? `
      <div class="search-page__empty-collections">
        ${collections.map((col) => `
          <a href="${escapeHtml(col.url)}" class="search-page__empty-collection">
            ${escapeHtml(col.title || '')}
          </a>
        `).join('')}
      </div>
    `
    : '';

  return `
    <div class="search-page__empty">
      <h2 class="search-page__empty-title">${escapeHtml(heading)}${query}</h2>
      ${subtext ? `<p class="search-page__empty-copy">${escapeHtml(subtext)}</p>` : ''}
      ${collectionsHtml}
    </div>
  `;
}
